import { firstValueFrom } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services';

/**
 * Runs once at app startup (via provideAppInitializer). In SSO mode it:
 *   1. initializes MSAL (required before any MSAL call in v3+),
 *   2. processes a redirect response if we're returning from Entra,
 *   3. sets the active account, and
 *   4. loads the local /user/me profile so guards/components have the role synchronously.
 *
 * In JWT mode it is a no-op, so the legacy login path is completely unaffected.
 */
export async function initializeSso(msalService: MsalService, authService: AuthService): Promise<void> {
    if (!environment.sso.enabled) {
        return;
    }

    await msalService.instance.initialize();

    // Wrap the redirect handling: in a private/incognito window a redirect can be interrupted or
    // throw (partitioned storage / blocked third-party cookies), which would otherwise reject the
    // app initializer (blank app) or leave MSAL's interaction state stuck and loop the sign-in.
    // Swallow so the app still boots; the guard then starts a clean sign-in.
    let redirectResult = null;
    try {
        redirectResult = await msalService.instance.handleRedirectPromise();
    } catch (e) {
        console.error('MSAL handleRedirectPromise failed; continuing to a fresh sign-in.', e);
    }
    if (redirectResult?.account) {
        msalService.instance.setActiveAccount(redirectResult.account);
    } else {
        const accounts = msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
            msalService.instance.setActiveAccount(accounts[0]);
        }
    }

    // If signed in, pull the local profile (role/id/etc.). If the user authenticated with Entra
    // but isn't provisioned in tblUser, /user/me 403s and loadSsoProfile leaves the profile null -
    // guards then deny access (the "admin must pre-provision" behavior).
    if (msalService.instance.getActiveAccount()) {
        try {
            await firstValueFrom(authService.loadSsoProfile());
        } catch {
            /* swallow - unauthenticated/unprovisioned users are handled by the guards */
        }
    }
}
